import React from 'react';

import TimeSeriesProvider from 'src/components/TimeSeriesProvider';
import TimeSeriesViz, {
  SOLO_VIZ_HEIGHT,
  SOLO_VIZ_HEIGHT_WITH_LEGEND,
} from 'src/components/TimeSeriesViz';
import {useTranslations} from 'src/components/TranslationsContext';
import Viz from 'src/components/Viz';
import {
  filterConfigToAvailableTimeSeries,
  makeRequestFromVizConfig,
} from 'src/components/Viz/config';
import WithWidth from 'src/components/WithWidth';
import {useSelectedForecastTimes} from 'src/hooks/useSelectedForecastTimes';
import {Site} from 'src/lib/api/project';
import {Source} from 'src/lib/api/series';
import {getSeasonalHorizon} from 'src/lib/siteUtils';

import {makeRegionalHFConfig} from './vizConfigs';

/**
 * Custom x-axis tick formatter for the regional report's seasonal charts.
 * The forecast horizon extends past the calendar year (ticks cross into the
 * following year), so the default "MMM YYYY" formatter produces a string of
 * "Jan 2027 / Feb 2027" labels that look like stale data. This formatter shows
 * the year only on January ticks (and the first tick of the chart) — mid-year
 * ticks show just "MMM", which reads more naturally for a water-year forecast.
 */
function regionalReportTickFormat(date: Date, timeZone: string): string {
  const monthName = new Intl.DateTimeFormat('en-US', {month: 'short', timeZone}).format(date);
  const month = Number(new Intl.DateTimeFormat('en-US', {month: 'numeric', timeZone}).format(date));
  if (month === 1) {
    const year = new Intl.DateTimeFormat('en-US', {year: 'numeric', timeZone}).format(date);
    return `${monthName} ${year}`;
  }
  return monthName;
}

/**
 * Per-site seasonal flow chart for the regional report. Mirrors the Seasonal3
 * flow pipeline (forecast quantile bands + LTA + optional NWRFC ESP) but
 * without the staging toggle, forecast-time picker, weather panels, or
 * section chrome — the report always shows the latest forecast in release
 * mode.
 */
export default function SiteForecastViz({site, dateRange}: {site: Site; dateRange: [Date, Date]}) {
  const {t} = useTranslations();
  const horizonDays = getSeasonalHorizon(site);

  const hfConfig = React.useMemo(
    () => filterConfigToAvailableTimeSeries(makeRegionalHFConfig(site, t), site),
    [site, t]
  );
  const forecastSources = React.useMemo(() => hfConfig.forecasts.map((c) => c.source), [hfConfig]);

  const {selectedForecastTimesBySource, forecastTimesAreLoading} = useSelectedForecastTimes(
    site,
    dateRange,
    horizonDays,
    Source.HydroForecastSeasonal,
    forecastSources,
    true,
    false
  );

  return (
    <WithWidth>
      {(width) => (
        <TimeSeriesProvider
          site={site}
          forecastTimesBySource={selectedForecastTimesBySource}
          forecastTimesAreLoading={forecastTimesAreLoading}
          dateRange={dateRange}
          isStaging={false}
          {...makeRequestFromVizConfig(hfConfig)}
        >
          {(vizProps) => (
            <Viz
              section=""
              title={site.name}
              site={site}
              loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
              dateRange={dateRange}
              config={hfConfig}
              {...vizProps}
            >
              {(props) => (
                <TimeSeriesViz
                  {...props}
                  timeZone={site.localTimeZone}
                  dims={[width, SOLO_VIZ_HEIGHT]}
                  xTickFormat={regionalReportTickFormat}
                />
              )}
            </Viz>
          )}
        </TimeSeriesProvider>
      )}
    </WithWidth>
  );
}
